import { useMemo } from 'react';

export default function RiskBreakdownRadar({ siteScore = {}, size = 240 }) {
  const factors = useMemo(() => [
    { name: 'Alert Freq', key: 'alertFreqScore', color: '#3b82f6', weight: 35 },
    { name: 'Severity', key: 'severityScore', color: '#ec4899', weight: 30 },
    { name: 'Compliance', key: 'complianceScore', color: '#f59e0b', weight: 20 },
    { name: 'Incidents', key: 'incidentScore', color: '#ef4444', weight: 15 },
  ], []);

  if (!siteScore || Object.keys(siteScore).length === 0) {
    return (
      <div className="flex items-center justify-center" style={{ width: size, height: size }}>
        <p className="text-sm text-slate-400">No data</p>
      </div>
    );
  }

  const center = size / 2;
  const radius = (size / 2) * 0.7;
  const levels = 5;

  // Calculate angle for each factor
  const angleSlice = (Math.PI * 2) / factors.length;

  // Get radial coordinates
  const getRadialCoord = (index, value, maxValue = 100) => {
    const angle = angleSlice * index - Math.PI / 2;
    const r = (value / maxValue) * radius;
    return {
      x: center + r * Math.cos(angle),
      y: center + r * Math.sin(angle),
    };
  };

  // Build SVG elements
  const gridLines = Array.from({ length: levels }, (_, i) => {
    const r = (radius / levels) * (i + 1);
    const points = factors
      .map((_, idx) => {
        const angle = angleSlice * idx - Math.PI / 2;
        return `${center + r * Math.cos(angle)},${center + r * Math.sin(angle)}`;
      })
      .join(' ');
    return (
      <polygon
        key={`grid-${i}`}
        points={points}
        fill="none"
        stroke="#e2e8f0"
        strokeWidth="1"
      />
    );
  });

  // Axis lines
  const axisLines = factors.map((_, idx) => {
    const angle = angleSlice * idx - Math.PI / 2;
    const x2 = center + radius * Math.cos(angle);
    const y2 = center + radius * Math.sin(angle);
    return (
      <line
        key={`axis-${idx}`}
        x1={center}
        y1={center}
        x2={x2}
        y2={y2}
        stroke="#cbd5e1"
        strokeWidth="1"
        strokeDasharray="2,2"
      />
    );
  });

  // Data polygon
  const dataPoints = factors.map((factor, idx) => {
    const value = siteScore[factor.key] || 0;
    const coord = getRadialCoord(idx, value);
    return coord;
  });

  const polygonPoints = dataPoints.map((p) => `${p.x},${p.y}`).join(' ');

  // Data circles
  const dataCircles = dataPoints.map((p, idx) => (
    <circle key={`data-${idx}`} cx={p.x} cy={p.y} r="3" fill={factors[idx].color} />
  ));

  // Labels
  const labels = factors.map((factor, idx) => {
    const coord = getRadialCoord(idx, 120, 100); // Position labels outside
    const value = Math.round(siteScore[factor.key] || 0);
    return (
      <g key={`label-${idx}`}>
        <text
          x={coord.x}
          y={coord.y}
          textAnchor="middle"
          className="text-xs font-semibold"
          fill="#475569"
        >
          {factor.name}
        </text>
        <text
          x={coord.x}
          y={coord.y + 12}
          textAnchor="middle"
          className="text-xs font-bold"
          fill={factor.color}
        >
          {value}
        </text>
      </g>
    );
  });

  // Center value (overall score)
  const overallScore = Math.round(siteScore.overallScore || 0);
  const overallColor =
    overallScore >= 75
      ? '#ef4444'
      : overallScore >= 50
      ? '#f97316'
      : overallScore >= 25
      ? '#eab308'
      : '#10b981';

  if (factors.every((f) => !siteScore[f.key])) {
    return (
      <div className="flex items-center justify-center" style={{ width: size, height: size }}>
        <p className="text-sm text-slate-400">Insufficient data</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center gap-3">
      <svg width={size} height={size} className="drop-shadow-sm">
        {/* Grid circles */}
        {gridLines}

        {/* Axis lines */}
        {axisLines}

        {/* Data area */}
        <polygon
          points={polygonPoints}
          fill={overallColor}
          fillOpacity="0.2"
          stroke={overallColor}
          strokeWidth="2"
        />

        {/* Data points */}
        {dataCircles}

        {/* Labels */}
        {labels}

        {/* Center circle with overall score */}
        <circle cx={center} cy={center} r="28" fill="white" stroke={overallColor} strokeWidth="2" />
        <text
          x={center}
          y={center - 5}
          textAnchor="middle"
          className="text-2xl font-black"
          fill={overallColor}
        >
          {overallScore}
        </text>
        <text
          x={center}
          y={center + 10}
          textAnchor="middle"
          className="text-[10px] font-semibold"
          fill="#64748b"
        >
          Risk Score
        </text>
      </svg>

      {/* Legend */}
      <div className="w-full">
        <div className="grid grid-cols-2 gap-2 text-xs">
          {factors.map((factor) => (
            <div key={factor.key} className="flex items-center gap-2">
              <div
                className="w-2 h-2 rounded-full"
                style={{ backgroundColor: factor.color }}
              />
              <span className="text-slate-600">
                {factor.name} <span className="font-semibold text-slate-700">{factor.weight}%</span>
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
